/**
 * Children Roster Report Tab
 * Generate reports of children by category with DOB, age, and sorting options
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { FileText, Download, Printer, Users, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { calculateAge } from "@/utils/helpers";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChildrenRosterTab = () => {
  const [children, setChildren] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Filter options
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState("none");
  const [includeStaffName, setIncludeStaffName] = useState(false);
  const [sortOrder, setSortOrder] = useState("name_asc"); // name_asc, name_desc, age_asc, age_desc
  
  // Report data
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [reportDate, setReportDate] = useState("");

  // Fetch children and users on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [childrenRes, usersRes] = await Promise.all([
        axios.get(`${API}/children?active_only=true`),
        axios.get(`${API}/users`)
      ]);
      setChildren(childrenRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    }
  };

  const categories = [
    { value: "all", label: "All Children" },
    { value: "full_time", label: "Full Time" },
    { value: "part_time", label: "Part Time" },
    { value: "school_age", label: "School Age" },
    { value: "after_school", label: "After School" },
    { value: "uncategorized", label: "Uncategorized" }
  ];

  const sortOptions = [
    { value: "name_asc", label: "Name (A-Z)", icon: ArrowUp },
    { value: "name_desc", label: "Name (Z-A)", icon: ArrowDown },
    { value: "age_asc", label: "Age (Youngest First)", icon: ArrowUp },
    { value: "age_desc", label: "Age (Oldest First)", icon: ArrowDown }
  ];

  // Calculate age in months for sorting
  const getAgeInMonths = (birthdate) => {
    if (!birthdate) return -1; // Put children without birthdate at the end
    try {
      const birthDate = new Date(birthdate + 'T00:00:00');
      const today = new Date();
      const years = today.getFullYear() - birthDate.getFullYear();
      const months = today.getMonth() - birthDate.getMonth();
      return years * 12 + months;
    } catch {
      return -1;
    }
  };

  const generateReport = () => {
    setLoading(true);
    
    // Filter by category
    let filtered = [...children];
    if (selectedCategory !== "all") {
      if (selectedCategory === "uncategorized") {
        filtered = filtered.filter(c => !c.category || !["full_time", "part_time", "school_age", "after_school", "regular"].includes(c.category));
      } else {
        filtered = filtered.filter(c => c.category === selectedCategory || (selectedCategory === "full_time" && c.category === "regular"));
      }
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortOrder) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "age_asc":
          // Youngest first = lowest age in months first (ascending order)
          return getAgeInMonths(a.birthdate) - getAgeInMonths(b.birthdate);
        case "age_desc":
          // Oldest first = highest age in months first (descending order)
          return getAgeInMonths(b.birthdate) - getAgeInMonths(a.birthdate);
        default:
          return a.name.localeCompare(b.name);
      }
    });
    
    setFilteredChildren(filtered);
    setReportDate(format(new Date(), "MMMM d, yyyy 'at' h:mm a"));
    setReportGenerated(true);
    setLoading(false);
    toast.success(`Report generated: ${filtered.length} children`);
  };

  const getStaffName = () => {
    if (!includeStaffName || !selectedStaff || selectedStaff === "none") return null;
    const staff = users.find(u => u.id === selectedStaff);
    return staff?.name || null;
  };

  const getCategoryLabel = (category) => {
    const map = {
      full_time: "Full Time",
      regular: "Full Time",
      part_time: "Part Time",
      school_age: "School Age",
      after_school: "After School"
    };
    return map[category] || "Uncategorized";
  };

  const formatBirthdate = (birthdate) => {
    if (!birthdate) return "-";
    try {
      return format(parseISO(birthdate), "M/d/yyyy");
    } catch {
      return birthdate;
    }
  };

  // Export to PDF
  const exportPDF = () => {
    if (!filteredChildren.length) {
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF();
    const staffName = getStaffName();
    const categoryLabel = categories.find(c => c.value === selectedCategory)?.label || "All Children";
    const sortLabel = sortOptions.find(s => s.value === sortOrder)?.label || "";
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(180, 83, 9); // Amber color
    doc.text("Children Roster Report", 14, 20);
    
    // Subtitle with details
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`Category: ${categoryLabel}`, 14, 30);
    doc.text(`Sorted by: ${sortLabel}`, 14, 36);
    doc.text(`Generated: ${reportDate}`, 14, 42);
    doc.text(`Total Children: ${filteredChildren.length}`, 14, 48);
    
    // Staff name if selected
    if (staffName) {
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Prepared by: ${staffName}`, 14, 56);
    }
    
    // Table
    const tableStartY = staffName ? 64 : 56;
    const tableData = filteredChildren.map((child, index) => [
      index + 1,
      child.name,
      formatBirthdate(child.birthdate),
      calculateAge(child.birthdate) || "-",
      getCategoryLabel(child.category)
    ]);

    autoTable(doc, {
      startY: tableStartY,
      head: [["#", "Child Name", "Date of Birth", "Age", "Category"]],
      body: tableData,
      headStyles: {
        fillColor: [245, 158, 11], // Amber-500
        textColor: [255, 255, 255],
        fontStyle: "bold"
      },
      alternateRowStyles: {
        fillColor: [254, 243, 199] // Amber-100
      },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 15 },
        1: { cellWidth: 60 },
        2: { halign: "center", cellWidth: 35 },
        3: { halign: "center", cellWidth: 25 },
        4: { cellWidth: 40 }
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
    }

    doc.save(`children_roster_${selectedCategory}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    toast.success("PDF downloaded");
  };

  // Export to CSV
  const exportCSV = () => {
    if (!filteredChildren.length) {
      toast.error("No data to export");
      return;
    }

    const staffName = getStaffName();
    const categoryLabel = categories.find(c => c.value === selectedCategory)?.label || "All Children";
    
    let csvContent = "";
    
    // Add header info
    csvContent += `Children Roster Report\n`;
    csvContent += `Category: ${categoryLabel}\n`;
    csvContent += `Generated: ${reportDate}\n`;
    csvContent += `Total Children: ${filteredChildren.length}\n`;
    if (staffName) {
      csvContent += `Prepared by: ${staffName}\n`;
    }
    csvContent += `\n`;
    
    // Add table headers
    csvContent += `#,Child Name,Date of Birth,Age,Category\n`;
    
    // Add data rows
    filteredChildren.forEach((child, index) => {
      csvContent += `${index + 1},"${child.name}","${formatBirthdate(child.birthdate)}","${calculateAge(child.birthdate) || "-"}","${getCategoryLabel(child.category)}"\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `children_roster_${selectedCategory}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    toast.success("CSV downloaded");
  };

  // Print report
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Filter Controls */}
      <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200 print:hidden">
        <CardHeader>
          <CardTitle className="text-xl text-blue-800 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Children Roster Report Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-blue-700">Category</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="border-blue-200" data-testid="roster-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort Order */}
            <div className="space-y-2">
              <Label className="text-blue-700">Sort By</Label>
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="border-blue-200" data-testid="roster-sort-select">
                  <SelectValue placeholder="Select sort order" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <opt.icon className="w-4 h-4" />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Staff Name Selection */}
            <div className="space-y-2">
              <Label className="text-blue-700">Staff Name (Optional)</Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger className="border-blue-200" data-testid="roster-staff-select">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Include Staff Name Checkbox */}
            <div className="flex items-end pb-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="includeStaff" 
                  checked={includeStaffName}
                  onCheckedChange={setIncludeStaffName}
                  data-testid="include-staff-checkbox"
                />
                <Label htmlFor="includeStaff" className="text-blue-700 cursor-pointer">
                  Include staff name on report
                </Label>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-center pt-4">
            <Button 
              onClick={generateReport}
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold px-8 py-2 rounded-full shadow-lg"
              data-testid="generate-roster-btn"
            >
              <Users className="w-5 h-5 mr-2" />
              {loading ? "Generating..." : "Generate Roster Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Display */}
      {reportGenerated && (
        <>
          {/* Export Buttons */}
          <div className="flex flex-wrap gap-2 print:hidden">
            <Button onClick={handlePrint} variant="outline" className="border-blue-300 text-blue-700">
              <Printer className="w-4 h-4 mr-2" />Print
            </Button>
            <Button onClick={exportPDF} variant="outline" className="border-red-300 text-red-700">
              <FileText className="w-4 h-4 mr-2" />Export PDF
            </Button>
            <Button onClick={exportCSV} variant="outline" className="border-green-300 text-green-700">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
          </div>

          {/* Report Card */}
          <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200" data-testid="roster-report-card">
            <CardHeader className="print:pb-2">
              <div className="flex flex-col gap-1">
                <CardTitle className="text-2xl text-blue-800">Children Roster Report</CardTitle>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Category: <span className="font-medium text-blue-700">{categories.find(c => c.value === selectedCategory)?.label}</span></p>
                  <p>Sorted by: <span className="font-medium text-blue-700">{sortOptions.find(s => s.value === sortOrder)?.label}</span></p>
                  <p>Generated: <span className="font-medium">{reportDate}</span></p>
                  <p>Total Children: <span className="font-bold text-blue-600">{filteredChildren.length}</span></p>
                  {includeStaffName && getStaffName() && (
                    <p className="text-base font-semibold text-gray-800 mt-2 pt-2 border-t">
                      Prepared by: {getStaffName()}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredChildren.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" data-testid="roster-table">
                    <thead>
                      <tr className="bg-blue-100">
                        <th className="border border-blue-200 p-2 text-center text-blue-800 w-12">#</th>
                        <th className="border border-blue-200 p-2 text-left text-blue-800">Child Name</th>
                        <th className="border border-blue-200 p-2 text-center text-blue-800">Date of Birth</th>
                        <th className="border border-blue-200 p-2 text-center text-blue-800">Age</th>
                        <th className="border border-blue-200 p-2 text-left text-blue-800">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChildren.map((child, index) => (
                        <tr key={child.id} className="hover:bg-blue-50">
                          <td className="border border-blue-200 p-2 text-center text-gray-500">{index + 1}</td>
                          <td className="border border-blue-200 p-2 font-medium">{child.name}</td>
                          <td className="border border-blue-200 p-2 text-center text-gray-600">
                            {formatBirthdate(child.birthdate)}
                          </td>
                          <td className="border border-blue-200 p-2 text-center text-blue-600 font-medium">
                            {calculateAge(child.birthdate) || "-"}
                          </td>
                          <td className="border border-blue-200 p-2 text-blue-600">
                            {getCategoryLabel(child.category)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No children found for the selected category</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Initial State */}
      {!reportGenerated && (
        <Card className="bg-blue-50 border-2 border-blue-200">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <p className="text-xl text-blue-700">
              Select options above and generate a roster report
            </p>
            <p className="text-sm text-blue-500 mt-2">
              View children by category with birthdates and ages, sorted ascending or descending
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChildrenRosterTab;
